import { StoreTenant } from '@/src/lib/store/types';
import { REQUEST_OFFER_MAIL, SUPPORT_MAIL } from '@/src/lib/core/config';

const PURCHASE_ORDER_NUMBER_LENGTH_SAP = 23;
const PURCHASE_ORDER_NUMBER_LENGTH_NETSUITE = 45;
const PURCHASE_ORDER_NUMBER_LENGTH_ABRA = 23;

export type TenantConfig = {
  salesPhone?: string;
  salesCenters?: Array<{ label: string; numbers: Array<string> }>;
  newsLetterLink: { [key: string]: string };
  supportMail: string;
  requestOfferMail: string;
  defaultLanguage: string;
  locales: Array<string>;
  zipCodeExample: {
    [key: string]: string;
    default: string;
  };
  localeMap: {
    [key: string]: string;
  };
  privacyStatementUrl: {
    [key: string]: string;
  };
  userAgreementUrl: {
    [key: string]: string;
  };
  purchaseOrderNumberMaxLength: number;
};

export const TenantsConfig: Record<StoreTenant | 'default', TenantConfig> = {
  default: {
    salesPhone: '',
    newsLetterLink: {
      en: 'https://www.turck.de/en/newsletter-registration-8358.php',
    },
    privacyStatementUrl: {
      en: 'https://www.turck.de/en/privacy-statement-2878.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'en',
    locales: ['en'],
    localeMap: {
      en: 'en-US',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '12345' },
  },
  [StoreTenant.NL]: {
    salesPhone: '+31 38 422 77 50',
    newsLetterLink: {
      nl: 'https://www.turck.nl/nl/nieuwsbrief-8358.php',
      en: 'https://www.turck.nl/nl/nieuwsbrief-8358.php',
    },
    privacyStatementUrl: {
      nl: 'https://www.turck.nl/nl/privacyverklaring-2878.php',
      en: 'https://www.turck.nl/nl/privacyverklaring-2878.php',
    },
    userAgreementUrl: {
      nl: '/nl/cms/webshop-terms',
      en: '/en/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'en',
    locales: ['nl', 'en'],
    localeMap: {
      nl: 'nl-NL',
      en: 'en-US',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '1234 AB' },
  },
  [StoreTenant.PL]: {
    salesPhone: '+48 77 55 62 120',
    newsLetterLink: {
      pl: 'https://www.turck.pl/pl/formularz-kontaktowy-114.php',
      en: 'https://www.turck.pl/pl/formularz-kontaktowy-114.php',
    },
    privacyStatementUrl: {
      pl: 'https://www.turck.pl/pl/polityka-prywatnosci-2878.php',
      en: 'https://www.turck.pl/pl/polityka-prywatnosci-2878.php',
    },
    userAgreementUrl: {
      pl: '/pl/cms/warunki-sprzedaży-sklepu-internetowego',
      en: '/en/cms/user-agreement',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'pl',
    locales: ['en', 'pl'],
    localeMap: {
      en: 'en-US',
      pl: 'pl-PL',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '11-111' },
  },
  [StoreTenant.AU]: {
    salesPhone: '+000 00 000 00 00',
    salesCenters: [{ label: 'Australia', numbers: ['1300 132 566', '+61 3 8544 7200'] }],
    newsLetterLink: { en: 'https://turck.us18.list-manage.com/subscribe?u=f0402a08cdeb0e4d8a6091de5&id=4c3c485dee' },
    privacyStatementUrl: {
      en: 'https://www.turck.com.au/en/privacy-statement-2878.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'en',
    locales: ['en'],
    localeMap: {
      en: 'en-US',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_NETSUITE,
    zipCodeExample: { default: '4567 or 45672', NZ: '8901' },
  },
  [StoreTenant.HU]: {
    salesPhone: '+36 1 477 07 40',
    newsLetterLink: {
      en: 'https://www.turck.hu/hu/hirlevel-feliratkozas-8358.php',
      hu: 'https://www.turck.hu/hu/hirlevel-feliratkozas-8358.php',
    },
    privacyStatementUrl: {
      en: 'https://www.turck.hu/hu/adatvedelmi-nyilatkozat-2878.php',
      hu: 'https://www.turck.hu/hu/adatvedelmi-nyilatkozat-2878.php',
    },
    userAgreementUrl: {
      hu: '/hu/cms/webshop-terms',
      en: '/en/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'hu',
    locales: ['en', 'hu'],
    localeMap: {
      en: 'en-US',
      hu: 'hu-HU',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '8901' },
  },
  [StoreTenant.BE]: {
    salesPhone: '+32 53 76 65 66',
    newsLetterLink: {
      en: 'https://www.turck.de/en/newsletter-registration-8358.php',
      de: 'https://www.turck.de/en/newsletter-registration-8358.php',
      fr: 'https://www.turck.de/en/newsletter-registration-8358.php',
      nl: 'https://www.turck.de/en/newsletter-registration-8358.php',
    },
    privacyStatementUrl: {
      en: 'https://www.multiprox.be/nl/privacy-statement-2878.php',
      de: 'https://www.multiprox.be/nl/privacy-statement-2878.php',
      fr: 'https://www.multiprox.be/fr/privacy-statement-2878.php',
      nl: 'https://www.multiprox.be/nl/privacy-statement-2878.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
      de: '/de/cms/webshop-terms',
      fr: '/fr/cms/webshop-terms',
      nl: '/nl/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'en',
    locales: ['en', 'de', 'fr', 'nl'],
    localeMap: {
      en: 'en-US',
      de: 'de-DE',
      fr: 'fr-FR',
      nl: 'nl-NL',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '8901' },
  },
  [StoreTenant.SE]: {
    salesPhone: '+46 (0) 104471600',
    newsLetterLink: {
      en: 'https://www.turck.de/en/newsletter-registration-8358.php',
      sv: 'https://www.turck.de/en/newsletter-registration-8358.php',
    },
    privacyStatementUrl: {
      en: 'https://www.turck.se/sma/legal-notice-128.php',
      sv: 'https://www.turck.se/sma/legal-notice-128.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
      sv: '/sv/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'en',
    locales: ['en', 'sv'],
    localeMap: {
      en: 'en-US',
      sv: 'sv-SE',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '123 45' },
  },
  [StoreTenant.DE]: {
    salesPhone: '+49 208 4952-380',
    newsLetterLink: {
      de: 'https://www.turck.de/de/newsletter-8358.php',
      en: 'https://www.turck.de/en/newsletter-registration-8358.php',
    },
    privacyStatementUrl: {
      en: 'https://www.turck.de/en/privacy-statement-2878.php',
      de: 'https://www.turck.de/de/datenschutz-2878.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
      de: '/de/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'de',
    locales: ['en', 'de'],
    localeMap: {
      en: 'en-US',
      de: 'de-DE',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '12345' },
  },
  [StoreTenant.MX]: {
    salesPhone: '+52 (844) 4116650',
    newsLetterLink: {
      en: 'https://www.turck.com.mx/es/declaracion-de-privacidad-2878.php',
      es: 'https://www.turck.com.mx/es/declaracion-de-privacidad-2878.php',
    },
    privacyStatementUrl: {
      en: 'https://www.turck.com.mx/es/declaracion-de-privacidad-2878.php',
      es: 'https://www.turck.com.mx/es/declaracion-de-privacidad-2878.php',
    },
    userAgreementUrl: {
      en: '/en/cms/webshop-terms',
      es: '/es/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'es',
    locales: ['en', 'es'],
    localeMap: {
      en: 'en-US',
      es: 'es-MX',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '12345' },
  },
  [StoreTenant.AT]: {
    salesPhone: '',
    newsLetterLink: {
      de: 'https://www.turck.de/de/newsletter-registration-8358.php',
      en: 'https://www.turck.de/en/newsletter-registration-8358.php',
    },
    privacyStatementUrl: {
      de: 'https://www.turck.de/de/privacy-statement-2878.php',
      en: 'https://www.turck.de/en/privacy-statement-2878.php',
    },
    userAgreementUrl: { de: '/de/cms/webshop-terms', en: '/en/cms/webshop-terms' },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'de',
    locales: ['de', 'en'],
    localeMap: {
      de: 'de-AT',
      en: 'en-US',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '1234' },
  },
  [StoreTenant.FR]: {
    salesPhone: '+33 (0)1 60 43 60 70',
    newsLetterLink: {
      fr: 'https://forms.info.turckbanner.fr/67ee42cd7e63793e2f2ed98d/yIbQkg8RQYqMT5oo8oByjQ/form.html',
      en: 'https://forms.info.turckbanner.fr/67ee42cd7e63793e2f2ed98d/yIbQkg8RQYqMT5oo8oByjQ/form.html',
    },
    privacyStatementUrl: {
      fr: 'https://www.turckbanner.fr/fr/politique-de-confidentialite-2878.php',
      en: 'https://www.turckbanner.fr/fr/politique-de-confidentialite-2878.php',
    },
    userAgreementUrl: {
      fr: '/fr/cms/webshop-terms',
      en: '/en/cms/webshop-terms',
    },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'fr',
    locales: ['fr', 'en'],
    localeMap: {
      en: 'en-US',
      fr: 'fr-FR',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_SAP,
    zipCodeExample: { default: '12345' },
  },
  [StoreTenant.CZ]: {
    salesPhone: '+420 495 518 766',
    newsLetterLink: {
      cs: 'https://turck.us12.list-manage.com/subscribe?u=c2588423c9ad56dd2031e181f&id=4cb2cf694f',
      en: 'https://turck.us12.list-manage.com/subscribe?u=c2588423c9ad56dd2031e181f&id=4cb2cf694f',
    },
    privacyStatementUrl: {
      cs: 'https://www.turck.cz/cs/ochrana-osobnich-udaj-2878.php',
      en: 'https://www.turck.cz/cs/ochrana-osobnich-udaj-2878.php',
    },
    userAgreementUrl: { cs: '/cs/cms/webshop-terms', en: '/en/cms/webshop-terms' },
    supportMail: SUPPORT_MAIL,
    requestOfferMail: REQUEST_OFFER_MAIL,
    defaultLanguage: 'cs',
    locales: ['cs', 'en'],
    localeMap: {
      cs: 'cs-CZ',
      en: 'en-US',
    },
    purchaseOrderNumberMaxLength: PURCHASE_ORDER_NUMBER_LENGTH_ABRA,
    zipCodeExample: { default: '123 45' },
  },
};
